// Admin — products with no usable weight or size, and asking HioBuy for them
// (owner, 2026-10-27). HioBuy asks the 1688 supplier by hand until their
// Supplier Inquiry API launches: we send product links, they send back the
// weight and packed size, and admin_market_supplier_measures stores them as
// measurements (market_sku_measures source 'supplier', never overwritten by a
// re-import). The list, the "sent" mark and the import are admin-only RPCs.
//
// The spreadsheet is CSV with a UTF-8 BOM so Excel opens Arabic and Chinese
// titles correctly. One row per product: the weight applies to every variant
// unless HioBuy adds a row per variant with its sku_key.

import { marketRpc } from './marketDb';

// 'noreal' = no real weight at all: nothing, or only an AI estimate.
export const LEVELS = ['noreal', 'none', 'ai', 'weight'];
export const STATUSES = ['open', 'requested', 'answered'];
// demand: our customers first (quotes 30 days, then paid orders), then 1688 sales.
export const SORTS = ['demand', 'sold', 'newest'];
export const PAGE = 50;

export const listMissingWeights = ({ level = 'noreal', status = 'open', offset = 0, limit = PAGE, sort = 'demand', demandOnly = false, showcaseOnly = false, category = '', q = '' } = {}) =>
  marketRpc('admin_market_missing_weights', {
    p_level: level, p_status: status, p_limit: limit, p_offset: offset, p_sort: sort,
    p_demand_only: !!demandOnly, p_showcase_only: !!showcaseOnly, p_category: category || null, p_q: String(q || '').trim().slice(0, 100) || null,
  });
export const markRequested = (ids, note) => marketRpc('admin_market_weight_inquiry_mark', { p_product_ids: ids, p_note: note || null });
export const saveSupplierMeasures = (rows) => marketRpc('admin_market_supplier_measures', { p_rows: rows });

/** The product's page on its source marketplace — admin-only, never shown to a customer. */
export function sourceLink(p) {
  if (p?.source_url) return p.source_url;
  if (p?.channel === '1688' && p?.source_product_id) return `https://detail.1688.com/offer/${p.source_product_id}.html`;
  return '';
}

export const CSV_COLUMNS = ['maabar_id', 'sku_key', 'product_link', 'title_en', 'title_zh', 'unit_weight_kg', 'length_cm', 'width_cm', 'height_cm', 'carton_qty', 'notes'];

const cell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The spreadsheet for HioBuy: one row per product, the answer columns empty. */
export function buildInquiryCsv(products) {
  const lines = [CSV_COLUMNS.join(',')];
  for (const p of products) lines.push([p.id, '', sourceLink(p), p.title_en || '', p.title_zh || '', '', '', '', '', '', ''].map(cell).join(','));
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** The message for HioBuy, in English, listing the products by link. */
export function buildInquiryMessage(products) {
  const list = products.map((p, i) => `${i + 1}. ${sourceLink(p) || '(no link)'} — ID ${p.id}${p.title_en ? ` — ${p.title_en}` : ''}`).join('\n');
  return [
    'Dear HIOBuy Team,',
    '',
    `As offered, could you please ask the suppliers for the shipping data of the ${products.length} product${products.length === 1 ? '' : 's'} below? For each one we need:`,
    '• the weight of one unit (kg), including its retail packaging;',
    '• the packed size of one unit: length × width × height (cm);',
    '• the carton quantity, and any difference between variants (size, colour).',
    '',
    'The attached spreadsheet lists the same products. Please fill in unit_weight_kg, length_cm, width_cm, height_cm and carton_qty (and notes where needed) and send it back unchanged otherwise. Keep the maabar_id column as it is. If variants differ, add a row per variant with its name in notes.',
    '',
    list,
    '',
    'Thank you,',
    'MAABAR Team',
  ].join('\n');
}

/**
 * RFC-4180-ish CSV → array of arrays (quotes, doubled quotes, delimiters and
 * newlines inside quotes). The delimiter is read off the header line: Excel
 * saves with ";" in some locales and a re-saved sheet may come back as TSV.
 */
export function parseCsv(text) {
  const s = String(text || '').replace(/^﻿/, '');
  const head = s.split(/\r?\n/, 1)[0];
  const count = (c) => head.split(c).length - 1;
  const sep = [',', ';', '\t'].reduce((best, c) => (count(c) > count(best) ? c : best), ',');
  const rows = []; let row = []; let f = ''; let q = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') { if (s[i + 1] === '"') { f += '"'; i++; } else q = false; } else f += ch;
    } else if (ch === '"') q = true;
    else if (ch === sep) { row.push(f); f = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && s[i + 1] === '\n') i++; row.push(f); rows.push(row); row = []; f = ''; }
    else f += ch;
  }
  if (f !== '' || row.length) { row.push(f); rows.push(row); }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
}

const ALIASES = {
  product_id: ['maabar_id', 'product_id', 'id'],
  sku_key: ['sku_key', 'sku'],
  unit_kg: ['unit_weight_kg', 'weight_kg', 'unit_kg', 'weight'],
  length_cm: ['length_cm', 'length', 'l'],
  width_cm: ['width_cm', 'width', 'w'],
  height_cm: ['height_cm', 'height', 'h'],
};
const num = (v) => {
  const t = String(v ?? '').trim().replace(/,/g, '.').replace(/[^\d.]/g, '');
  return t === '' ? null : Number(t);
};

/**
 * HioBuy's filled spreadsheet → rows for admin_market_supplier_measures.
 * Rows with no weight are skipped (not answered yet), not errors. The server
 * checks everything again (range, plausibility, the product and SKU exist).
 */
export function parseMeasuresCsv(text) {
  const grid = parseCsv(text);
  if (!grid.length) return { rows: [], skipped: 0, error: 'empty' };
  const head = grid[0].map((h) => String(h).trim().toLowerCase());
  const col = Object.fromEntries(Object.entries(ALIASES).map(([k, names]) => [k, head.findIndex((h) => names.includes(h))]));
  if (col.product_id < 0 || col.unit_kg < 0) return { rows: [], skipped: 0, error: 'missing_columns' };
  const rows = []; let skipped = 0;
  for (const r of grid.slice(1)) {
    const kg = num(r[col.unit_kg]);
    if (kg == null) { skipped++; continue; }
    const pick = (k) => (col[k] >= 0 ? num(r[col[k]]) : null);
    rows.push({
      product_id: String(r[col.product_id] || '').trim(),
      sku_key: col.sku_key >= 0 ? String(r[col.sku_key] || '').trim() || null : null,
      unit_kg: kg, length_cm: pick('length_cm'), width_cm: pick('width_cm'), height_cm: pick('height_cm'),
    });
  }
  return { rows, skipped, error: null };
}

/** What a refused row's reason means, for the admin. */
export const REASONS = {
  ar: {
    not_a_number: 'رقم غير صالح', unknown_product: 'منتج غير معروف', weight_missing_or_out_of_range: 'الوزن ناقص أو خارج المدى (0.005–100 كجم)',
    size_needs_all_three: 'المقاس يحتاج الطول والعرض والارتفاع معًا', size_out_of_range: 'مقاس خارج المدى (0.5–300 سم)',
    weight_size_implausible: 'الوزن لا يتناسب مع المقاس', unknown_sku: 'خيار المنتج غير معروف', no_skus: 'المنتج بلا خيارات',
    warehouse_measured: 'مقاس من المستودع موجود — لم يُستبدل',
  },
  en: {
    not_a_number: 'Not a number', unknown_product: 'Unknown product', weight_missing_or_out_of_range: 'Weight missing or out of range (0.005–100 kg)',
    size_needs_all_three: 'Size needs length, width and height together', size_out_of_range: 'Size out of range (0.5–300 cm)',
    weight_size_implausible: 'Weight does not fit the size', unknown_sku: 'Unknown variant', no_skus: 'Product has no variants',
    warehouse_measured: 'A warehouse measurement exists — not replaced',
  },
};
